import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CreditCard, Download, Eye, PackageCheck, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { orderApi } from '../services/api';
import UserPanelShell from '../components/UserPanelShell';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN');
const statuses = ['Confirmed', 'Packing', 'Packed', 'Dispatched', 'Delivered'];

const loadRazorpaySdk = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout'));
    document.body.appendChild(script);
  });

const OrdersPage = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    orderApi.mine()
      .then((response) => {
        const backendOrders = response.data?.data || [];
        setOrders(backendOrders);
        if (id) {
          const match = backendOrders.find((order) => order._id === id);
          setSelected(match);
        }
      })
      .catch(() => {
        setOrders([]);
        if (id) setSelected(null);
      });
  }, [user, id]);

  const order = useMemo(() => selected, [selected]);

  if (!user) return <Navigate to="/login" replace />;

  const submitReturn = async () => {
    if (!order || !reason.trim()) return;
    setMessage('');
    try {
      await orderApi.requestReturn(order._id, { reason, images: [] });
      setMessage('Return request submitted. Support will review it.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not submit return request.');
    }
  };

  const cancelOrder = async () => {
    if (!order) return;
    setMessage('');
    try {
      const response = await orderApi.cancel(order._id, { reason: 'Cancelled by customer before dispatch' });
      setSelected(response.data?.data);
      setMessage('Order cancelled. Stock has been restored.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not cancel order.');
    }
  };

  const downloadReceipt = async (orderId) => {
    try {
      const response = await orderApi.downloadReceipt(orderId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `fosen-receipt-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not download receipt.');
    }
  };

  const payRemainingBalance = async () => {
    if (!order || Number(order.paymentSettlement?.balanceDue || 0) <= 0) return;
    setMessage('');
    try {
      const amountDue = Number(order.paymentSettlement.balanceDue || 0);
      const paymentOrderResponse = await orderApi.createPaymentOrder({ amount: amountDue });
      const paymentOrder = paymentOrderResponse.data?.data;
      if (!paymentOrder?.keyId || String(paymentOrder.id || '').startsWith('mock_order_')) {
        const response = await orderApi.verifyRemainingPayment(order._id, {
          orderId: paymentOrder?.id || `test_order_${Date.now()}`,
          paymentId: `test_payment_${Date.now()}`,
          signature: 'test_signature',
          amount: amountDue
        });
        setSelected(response.data?.data);
        setMessage('Remaining payment recorded. Delivery OTP has been sent to your email.');
        return;
      }

      await loadRazorpaySdk();
      const paymentResult = await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: paymentOrder.keyId,
          amount: paymentOrder.amount,
          currency: paymentOrder.currency || 'INR',
          name: 'FOSEN Furniture',
          description: 'Remaining order payment',
          order_id: paymentOrder.id,
          handler: resolve,
          modal: { ondismiss: () => reject(new Error('Payment cancelled')) }
        });
        razorpay.on('payment.failed', (response) => reject(new Error(response.error?.description || 'Payment failed')));
        razorpay.open();
      });

      const response = await orderApi.verifyRemainingPayment(order._id, {
        orderId: paymentResult.razorpay_order_id,
        paymentId: paymentResult.razorpay_payment_id,
        signature: paymentResult.razorpay_signature,
        amount: amountDue
      });
      setSelected(response.data?.data);
      setMessage('Remaining payment successful. Delivery OTP has been sent to your email.');
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || 'Could not complete remaining payment.');
    }
  };

  if (id) {
    if (!order) {
      return (
        <main className="bg-[#FAF7F2] min-h-screen py-20 text-center">
          <p className="text-[#1F2A44]">Order not found.</p>
          <Link to="/orders" className="text-[#E8B530]">Back to orders</Link>
        </main>
      );
    }

    const currentIndex = Math.max(0, statuses.indexOf(order.orderStatus));

    return (
      <UserPanelShell title="Track Order" subtitle="Review tracking, receipt details, and return requests.">
          <Link to="/orders" className="text-sm text-[#1F2A44]/70 hover:text-[#E8B530]">Back to orders</Link>
          <div className="bg-white border border-[#1F2A44]/10 p-6 mt-4">
            <div className="flex items-center gap-2"><Truck className="text-[#E8B530]" /><h1 className="font-serif text-2xl font-bold text-[#1F2A44]">Track Order</h1></div>
            <p className="text-sm text-[#1F2A44]/60 mt-1">Order ID: {order._id}</p>
            <button onClick={() => downloadReceipt(order._id)} className="mt-4 inline-flex items-center gap-2 bg-[#1F2A44] text-[#FAF7F2] px-4 py-2 text-sm font-semibold">
              <Download size={16} /> Download Receipt PDF
            </button>
            {Number(order.paymentSettlement?.balanceDue || 0) > 0 && (
              <button onClick={payRemainingBalance} className="mt-4 ml-3 inline-flex items-center gap-2 bg-[#E8B530] text-[#1F2A44] px-4 py-2 text-sm font-semibold">
                <CreditCard size={16} /> Pay Balance
              </button>
            )}

            <div className="grid md:grid-cols-5 gap-3 mt-6">
              {statuses.map((status, index) => (
                <div key={status} className={`border p-3 text-sm ${index <= currentIndex ? 'border-[#E8B530] bg-[#E8B530]/10 text-[#1F2A44]' : 'border-[#1F2A44]/10 text-[#1F2A44]/50'}`}>
                  <PackageCheck size={16} className="mb-2" />
                  {status}
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              {(order.trackingHistory || []).map((entry, index) => (
                <div key={`${entry.status}-${index}`} className="border-l-2 border-[#E8B530] pl-4 text-sm">
                  <p className="font-semibold text-[#1F2A44]">{entry.status}</p>
                  <p className="text-[#1F2A44]/60">{entry.date ? new Date(entry.date).toLocaleString() : 'Pending'}</p>
                  <p className="text-[#1F2A44]/75">{entry.note}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-5 border-t pt-5">
              <div>
                <h2 className="font-serif text-xl font-bold text-[#1F2A44]">Receipt Details</h2>
                <div className="mt-3 text-sm text-[#1F2A44]/75 space-y-1">
                  <p>User: {user.name} ({user.email})</p>
                  <p>Payment: {order.paymentInfo?.method || 'razorpay'} - {order.paymentInfo?.status || 'pending'}</p>
                  <p>Paid: {fmt(order.paymentSettlement?.paidAmount)} | Balance: {fmt(order.paymentSettlement?.balanceDue)}</p>
                  <p>Pickup/Delivery OTP: {order.handover?.otp || 'Will be generated after confirmation'}</p>
                  <p>Fulfillment: {order.fulfillment?.type === 'pickup' ? 'Pickup from store' : 'Home delivery'}</p>
                  {order.fulfillment?.type === 'pickup' ? (
                    <p>Store: {order.fulfillment?.storeSnapshot?.name}, {order.fulfillment?.storeSnapshot?.address?.city}</p>
                  ) : (
                    <p>Ship to: {order.shippingAddress?.fullAddress}, {order.shippingAddress?.city} - {order.shippingAddress?.pincode}</p>
                  )}
                </div>
              </div>
              <div className="text-sm text-[#1F2A44]/75 space-y-2">
                {(order.products || []).map((item) => (
                  <div key={`${item.productId || item.name}-${item.sku}`} className="flex justify-between gap-3 border-b border-[#1F2A44]/10 pb-2">
                    <span>{item.name} x {item.quantity}<br /><small>Rate {fmt(item.price)} Discount {item.discount || 0}%</small></span>
                    <strong>{fmt(item.lineTotal || item.price * item.quantity)}</strong>
                  </div>
                ))}
                <div className="flex justify-between"><span>Subtotal</span><strong>{fmt(order.subtotal)}</strong></div>
                <div className="flex justify-between"><span>GST</span><strong>{fmt(order.taxAmount)}</strong></div>
                <div className="flex justify-between"><span>Delivery/Pickup</span><strong>{fmt(order.deliveryCharge)}</strong></div>
                <div className="flex justify-between"><span>Paid</span><strong>{fmt(order.paymentSettlement?.paidAmount)}</strong></div>
                <div className="flex justify-between"><span>Balance Due</span><strong>{fmt(order.paymentSettlement?.balanceDue)}</strong></div>
                <div className="flex justify-between text-[#1F2A44] text-base"><span>Total</span><strong>{fmt(order.totalAmount)}</strong></div>
              </div>
            </div>

            <div className="mt-8 border-t pt-5">
              <h2 className="font-serif text-xl font-bold text-[#1F2A44]">Return & Refund</h2>
              {!['Dispatched', 'Shipped', 'Out For Delivery', 'Delivered', 'Returned', 'Refunded', 'Cancelled'].includes(order.orderStatus) && (
                <button onClick={cancelOrder} className="mt-3 mr-3 bg-red-600 text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider">
                  Cancel Order
                </button>
              )}
              <p className="text-sm text-[#1F2A44]/60 mt-1">Submit a return request with a reason. Admin can approve, reject, or process refund from the admin workflow.</p>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for return" className="w-full mt-3 border border-[#1F2A44]/20 px-4 py-3 text-sm min-h-[90px]" />
              <button onClick={submitReturn} className="mt-3 bg-[#1F2A44] text-[#FAF7F2] px-5 py-3 text-sm font-semibold uppercase tracking-wider">
                Request Return
              </button>
              {message && <p className="mt-3 text-sm text-[#1F2A44]/70">{message}</p>}
            </div>
          </div>
      </UserPanelShell>
    );
  }

  return (
    <UserPanelShell title="My Orders" subtitle="See every order, payment total, receipt, and current fulfillment status.">
        <div className="space-y-3 mt-6">
          {orders.length === 0 ? (
            <div className="bg-white border border-[#1F2A44]/10 p-8 text-center text-[#1F2A44]/70">No orders yet.</div>
          ) : (
            orders.map((order) => (
              <div key={order._id} className="bg-white border border-[#1F2A44]/10 p-4 hover:border-[#E8B530]">
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#1F2A44]">Order {order._id}</p>
                    <p className="text-sm text-[#1F2A44]/60">{order.products?.length || 0} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#1F2A44]">{fmt(order.totalAmount)}</p>
                    <p className="text-sm text-[#1F2A44]/60">{order.orderStatus}</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to={`/orders/${order._id}`} className="inline-flex items-center gap-2 text-sm text-[#1F2A44] hover:text-[#E8B530]"><Eye size={16} /> View Receipt</Link>
                  <button onClick={() => downloadReceipt(order._id)} className="inline-flex items-center gap-2 text-sm text-[#1F2A44] hover:text-[#E8B530]"><Download size={16} /> Download PDF</button>
                </div>
              </div>
            ))
          )}
        </div>
    </UserPanelShell>
  );
};

export default OrdersPage;
